// import * as configEN from './server-app-config-en'
import * as configDA from './server-app-config-da'
import * as configNO from './server-app-config-no'
import * as configSV from './server-app-config'
import { AppConfigBase, AppConfigTheme, SiteMetaData } from 'ecosystem'
import manifest from '../../public/manifest.json'
import { server } from '../custom/api/server'
import { Metadata } from 'next'
import { stdCanonicalFactory } from 'shared-utils'
import { CustomLocale, defaultLocale } from '#/src/i18n'
import { productAttributes } from '#/src/appConfig/productAttributes'

interface ServerConfig {
  serverAppConfig: AppConfigBase
  serverTheme: AppConfigTheme
}

export class AppConfig {
  private _config: ServerConfig

  constructor(locale: CustomLocale['locale']) {
    switch (locale) {
      case 'no': {
        const serverConfig = configNO as unknown as ServerConfig
        this._config = {
          ...serverConfig,
          serverAppConfig: {
            productAttributes: productAttributes.no,
            ...serverConfig.serverAppConfig
          }
        }
        break
      }
      case 'da': {
        const serverConfig = configDA as unknown as ServerConfig
        this._config = {
          ...serverConfig,
          serverAppConfig: {
            productAttributes: productAttributes.da,
            ...serverConfig.serverAppConfig
          }
        }
        break
      }
      // case 'en': {
      //   this._config = configEN as unknown as ServerConfig
      //   break
      // }
      case 'sv':
      default: {
        const serverConfig = configSV as unknown as ServerConfig
        this._config = {
          ...serverConfig,
          serverAppConfig: {
            productAttributes: productAttributes.sv,
            ...serverConfig.serverAppConfig,
            paths: {
              brandsUrlPath: '/varumarken',
              categoriesUrlPath: '/kategorier',
              tagsUrlPath: '/t',
              productsUrlPath: '/p',
              blogsUrlPath: '/blog'
            }
          }
        }
        break
      }
    }
  }

  public get siteMetadata(): SiteMetaData {
    return {
      title: manifest.name,
      description: manifest.description,
      image: `${this._config.serverAppConfig.logoUrl}`,
      url: server!,
      keywords: [
        'Vinlagring',
        'Vigneron',
        'Vinkyl',
        'Vinkällare',
        'wine',
        'storage',
        'wine storage'
      ]
    }
  }

  public get config(): AppConfigBase & { site: SiteMetaData; theme: AppConfigTheme } {
    return {
      ...this._config.serverAppConfig,
      site: this.siteMetadata,
      theme: this._config.serverTheme
    }
  }
}

export const appConfig = (locale?: CustomLocale['locale']) => {
  return new AppConfig(locale || defaultLocale)
}

export const defaultAppConfig = appConfig()

type MetadataFactory = Metadata & {
  locale: CustomLocale['locale']
  pathname: string
  title?: string
  description?: string
  images?: string[]
  keywords?: string[]
  /**
   * Site-level metadata to base this on. Defaults to the bundled per-locale
   * `appConfig(locale).siteMetadata`; pass `appConfig.site` from
   * `getDynamicAppConfig(locale)` so the OG/Twitter image follows the live store
   * logo without a redeploy.
   */
  site?: SiteMetaData
}
export const metadataFactory = ({
  locale,
  title,
  description,
  images,
  pathname,
  keywords,
  site,
  ...rest
}: MetadataFactory): Metadata => {
  // https://github.com/vercel/next.js/issues/35345
  const path = pathname?.replace(/^\/index/, '/')
  const completeUrl = `${defaultAppConfig.siteMetadata.url.replace(/\/+$/, '')}${path}`
  const siteMetadata = site ?? appConfig(locale).siteMetadata
  const fullTitle = title ? `${title} - ${siteMetadata.title}` : siteMetadata.title

  return {
    title: fullTitle,
    description: description || siteMetadata.description,
    keywords: [...siteMetadata.keywords, ...(keywords ? keywords : [])],
    openGraph: {
      url: completeUrl,
      type: 'website',
      title: fullTitle,
      description: description || siteMetadata.description,
      images: [siteMetadata.image, ...(images ? images : [])]
    },
    twitter: {
      title: fullTitle,
      description: description || siteMetadata.description,
      card: 'summary_large_image',
      images: [siteMetadata.image, ...(images ? images : [])]
    },
    alternates: {
      canonical: stdCanonicalFactory(path, server)
    },
    ...rest
  }
}
